import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {PageLoader} from '@mintdata-wsp/wsp/ui/components/pageLoader/PageLoader';
import {paths} from '@mintdata-wsp/wsp/ui/routes/Routing';
import {VisualDesignerContainer} from '@mintdata-wsp/wsp/ui/views/visualDesigner/container/VisualDesignerContainer';
import {IPageAndSheetToOpen, LocalStateUtil} from '@mintdata-wsp/wsp/util/LocalStateUtil';
import {SpinnerSize, SpinnerStyle} from '@mintdata/mint-kit/impl/components/spinner/Spinner';
import * as classnames from 'classnames/bind';
import {UnregisterCallback} from 'history';
import * as is from 'is_js';
import {action, computed, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {RouteComponentProps} from 'react-router';

const styles = require('./EditDesignPage.less');
const cx = classnames.bind(styles);

@observer
export class EditDesignPage extends React.Component<RouteComponentProps<any>, any>
{
    @observable private _isLoading:boolean = true;

    private _routerBlockDisposer:UnregisterCallback = null;

    public constructor(props:RouteComponentProps<any>)
    {
        super(props);

        if( is.not.desktop() )
        {
            controllers.navigation.navigateTo(paths.designs(), {match: this.props.match});
            return;
        }

        this._init();
    }

    public componentDidMount()
    {
        const {location} = this.props;
        const {ui: {openDesign}, navigation} = controllers;

        this._routerBlockDisposer = navigation.getCurrentRouteProps().history.block((targetLocation) =>
        {
            if( targetLocation.pathname !== location.pathname && !navigation.isFlowEditorPage(targetLocation.pathname) )
                openDesign.performDesignCleanup();
        });

        window.addEventListener('beforeunload', this._handleBeforeUnload);
    }

    public async componentDidUpdate(prevProps:RouteComponentProps<any>):Promise<void>
    {
        const {openDesign} = controllers.ui;

        if( prevProps.location !== this.props.location )
        {
            await openDesign.performDesignCleanup();
            await this._init();
        }
    }

    @action
    public componentWillUnmount()
    {
        window.removeEventListener('beforeunload', this._handleBeforeUnload);

        if( this._routerBlockDisposer )
            this._routerBlockDisposer();
    }

    private _handleBeforeUnload(e:BeforeUnloadEvent)
    {
        controllers.ui.openDesign.takeDesignScreenshot();
        return null;
    }

    @action.bound
    private _stopLoading():void
    {
        this._isLoading = false;
    }

    private _getUrlParams(props:RouteComponentProps<any>):{projectId:number, designId:number}
    {
        const {projectId, designId} = props.match.params;

        return {
            projectId: Number(projectId),
            designId: Number(designId)
        };
    }

    @action
    private async _init():Promise<void>
    {
        const {
            artifacts,
            workbenchVisibility,
            openDesign,
            workbench,
            flow,
            surfaceArea,
            multiplayer,
            spreadsheets
        } = controllers.ui;

        controllers.navigation.setCurrentRouteProps(this.props);

        const {projectId, designId} = this._getUrlParams(this.props);

        this._isLoading = true;

        const userId = await controllers.user.syncUserId();
            
        if (!rootState.userSession.currentUser.isEmailVerified)
        {
            controllers.navigation.toAppHome();
            return;
        }

        artifacts.resetArtifactsLoadingPromise();
        workbenchVisibility.setSurfaceCopyPagesPanelShown(false);

        if( !openDesign.getOpenDesign() )
        {
            flow.clearFlows();

            const isMultiplayerMode = location.hash.indexOf('multiplayer=no') === -1;
            const isMultiplayerBustCache = location.hash.indexOf('bust-cache=1') > -1;

            if( isMultiplayerMode )
            {
                await multiplayer.connect(projectId, designId, userId, isMultiplayerBustCache);
            }

            await openDesign.openDesign(projectId, designId, isMultiplayerMode);

            // When design is being opened, it's project becomes last used project.
            const orgSid = rootState.userSession.currentOrganization.siteId;
            LocalStateUtil.storeSelectedProject(projectId, orgSid);

            artifacts.startLoadingBundlesForVisualArtifacts();
            workbench.filterVisibleVisualComponents(null);

            artifacts.ensureCloudArtifactsAreLoaded();
        }

        const pageAndSheetInfo:IPageAndSheetToOpen = LocalStateUtil.getPageAndSheetToOpen();

        if( pageAndSheetInfo )
        {
            if( pageAndSheetInfo.page )
            {
                const page:Page = surfaceArea.getPageById(pageAndSheetInfo.page);
                page && surfaceArea.setActivePage(page);
            }

            if( pageAndSheetInfo.sheet )
            {
                const sheet:AnySheet = spreadsheets.main.findAnySheetById(pageAndSheetInfo.sheet);
                const workbook:Workbook = commonControllers.spreadsheets.main.getRootWorkbook();
                sheet && spreadsheets.tabs.setActiveSheet(workbook.id, sheet.id);
            }

            LocalStateUtil.clearPageAndSheetToOpen();
        }

        this._stopLoading();
        controllers.ui.anyPage.disablePageLoader();

        commonControllers.analytics.main.initAllVendors();
        this._sendMDAEventLoaded();
    }

    private _sendMDAEventLoaded()
    {
        const {currentOrganization} = rootState.userSession;
        const {projectId, designId} = this._getUrlParams(this.props);

        controllers.analytics.main.trackEvent(
            AnalyticsEventActions.WSP_DESIGNER_LOADED,
            {
                is_from_wsp: true,

                org_sid: currentOrganization.siteId,
                org_name: currentOrganization.name,

                space_id: projectId,
                // space_name: currentProject.name,

                design_id: designId
                // design_name: design.name,
            }
        );
    }

    private _isShowLoader():boolean
    {
        const globalPageLoader = rootState.ui.anyPage.isLoading;
        const isCopyPagesInProgress:boolean = controllers.ui.copyPages.isCopyInProgress();
        const thisPageLoader = this._isLoading || controllers.ui.flow.isNewFlowCreating();
        return (!globalPageLoader && thisPageLoader) || isCopyPagesInProgress;
    }

    @computed
    private get _loaderText():JSX.Element
    {
        const {copyPages} = controllers.ui;
        const isCopyPagesInProgress:boolean = copyPages.isCopyInProgress();

        if( !isCopyPagesInProgress )
            return null;

        return <span>Copying from <strong>{copyPages.getSelectedDesignName()}</strong>&hellip;</span>;
    }

    public render()
    {
        return (
            <div>
                <div className={cx('visualDesignerHolder')}>
                    <VisualDesignerContainer />
                </div>
                {
                    this._isShowLoader() &&
                    <PageLoader
                        spinnerProps={{size: SpinnerSize.Large_64, style: SpinnerStyle.White}}
                        text={this._loaderText}
                    />
                }
            </div>
        );
    }
}
